import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, ArrowRight, Loader2, CheckCircle2, Calendar, Sparkles, Globe, Users, Phone } from 'lucide-react';
import { submitDemoRequest } from '@/lib/api';
import { toast } from 'sonner';
import { ChatWidget } from '@/components/ChatWidget';
import MegaFooter from '@/components/MegaFooter';
import Navbar from '@/components/Navbar';
import SEOHead from '@/components/SEOHead';

// Logo
const LOGO_URL = "/logo.svg";




const WhatsAppFloat = () => (
  <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="whatsapp-float">
    <MessageCircle size={28} color="white" />
  </a>
);

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    company_size: '',
    channel_type: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitDemoRequest(formData);
      setSuccess(true);
      toast.success('Demo request submitted successfully!');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Navbar />
      <SEOHead title="Contact Us - Book a Channel Loyalty Demo" description="Book a personalized demo of ChannelLoyalty's B2B channel loyalty platform. Talk to our loyalty architects about dealer rewards, distributor incentives, and channel engagement programs." keywords="B2B loyalty demo, channel incentive consultation, dealer loyalty platform demo India" canonical="https://channelloyalty.ai/contact" />
      
      <main>
        {/* Banner Hero */}
        <section className="hero-banner-full">
          <div className="hero-banner-carousel">
            <div className="hero-banner-track">
              <div className="hero-banner-slide active">
                <img src="/images/banner_contact.jpg" alt="Contact ChannelLoyalty - Schedule Your Enterprise Demo" />
              </div>
            </div>
          </div>
        </section>

        {/* Form Section */}
        <section className="section">
          <div className="container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
              
              {/* Form */}
              <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                {success ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-gradient-to-r from-[#2C39B8] to-[#6FC8F5] rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="text-white" size={40} />
                    </div>
                    <h3 className="text-gray-900 text-2xl font-bold mb-2">Thank You!</h3>
                    <p className="text-gray-600">Our team will contact you within 24 hours to schedule your demo.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label className="form-label">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="form-input"
                        placeholder="John Smith"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="form-label">Work Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="form-input"
                        placeholder="john@company.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div>
                      <label className="form-label">Company *</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        className="form-input"
                        placeholder="Company Name"
                        data-testid="contact-company-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Channel Size</label>
                        <select
                          name="company_size"
                          value={formData.company_size}
                          onChange={handleChange}
                          className="form-input"
                        >
                          <option value="">Select size</option>
                          <option value="< 1000">Under 1,000</option>
                          <option value="1000-10000">1,000 - 10,000</option>
                          <option value="10000-100000">10,000 - 100,000</option>
                          <option value="100000+">100,000+</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Industry</label>
                        <select
                          name="channel_type"
                          value={formData.channel_type}
                          onChange={handleChange}
                          className="form-input"
                        >
                          <option value="">Select industry</option>
                          <option value="fmcg">FMCG</option>
                          <option value="pharma">Pharma</option>
                          <option value="paints">Paints</option>
                          <option value="auto">Auto</option>
                          <option value="electronics">Electronics</option>
                          <option value="building">Building Materials</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="form-label">Tell us about your needs</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        className="form-input h-auto py-3"
                        placeholder="Describe your current challenges and goals..."
                        data-testid="contact-message-input"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="btn btn-primary w-full"
                      data-testid="contact-submit-btn"
                    >
                      {loading ? <Loader2 className="animate-spin" size={18} /> : <>Request Demo <ArrowRight size={18} /></>}
                    </button>
                  </form>
                )}
              </div>

              {/* Side Cards */}
              <div className="space-y-6">
                {/* Team Contacts */}
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <h4 className="text-gray-900 font-bold mb-4 flex items-center gap-2">
                    <Users className="text-[#2C39B8]" size={20} />
                    Connect With Our Team
                  </h4>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-10 h-10 bg-gradient-to-r from-[#2C39B8] to-[#6FC8F5] rounded-full flex items-center justify-center text-white font-bold text-sm">CS</div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 text-sm">Chandra Shekhar</div>
                        <div className="text-gray-500 text-xs">chandra@channelloyalty.ai</div>
                      </div>
                      <a href="tel:+919910059861" className="text-[#2C39B8] hover:text-[#2C39B8]">
                        <Phone size={18} />
                      </a>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-[#6FC8F5] rounded-full flex items-center justify-center text-white font-bold text-sm">DL</div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 text-sm">Deepika Laxmi</div>
                        <div className="text-gray-500 text-xs">deepika@channelloyalty.ai</div>
                      </div>
                      <a href="tel:+919986023182" className="text-[#2C39B8] hover:text-[#2C39B8]">
                        <Phone size={18} />
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-gradient-to-r from-green-400 to-green-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="text-white" size={28} />
                    </div>
                    <div>
                      <h4 className="text-gray-900 font-bold mb-1">WhatsApp Direct</h4>
                      <p className="text-gray-600 text-sm mb-4">Chat with our sales team instantly.</p>
                      <a
                        href="https://wa.me/919910059861"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-whatsapp text-sm"
                      >
                        <MessageCircle size={16} /> Chat on WhatsApp
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-blue-50 rounded-2xl p-6 border border-blue-100">
                  <h4 className="text-gray-900 font-bold mb-4">What to Expect</h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-gradient-to-r from-[#2C39B8] to-[#6FC8F5] text-white text-xs flex items-center justify-center font-bold">1</span>
                      <span className="text-gray-600 text-sm">Discovery call to understand your channel structure</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-gradient-to-r from-[#2C39B8] to-[#6FC8F5] text-white text-xs flex items-center justify-center font-bold">2</span>
                      <span className="text-gray-600 text-sm">Custom demo tailored to your industry</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-full bg-gradient-to-r from-[#2C39B8] to-[#6FC8F5] text-white text-xs flex items-center justify-center font-bold">3</span>
                      <span className="text-gray-600 text-sm">Solution mapping and implementation roadmap</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <MegaFooter />
      <WhatsAppFloat />
      <ChatWidget />
    </div>
  );
}
